import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../api/axiosInstance';

/**
 * SuspensionBanner — top-of-page billing status banner.
 *
 * Shows for accounts in past_due, grace_period, suspended, deactivated, or expired states.
 * Replaces the old "blocked at login" UX so admins can always reach billing to recover.
 *
 * Tier mapping:
 *   past_due      → orange, "Payment failed, retrying"
 *   grace_period  → red,    "Update payment within N days"
 *   suspended     → red,    "Account suspended — admin access only"
 *   deactivated   → red,    "Account deactivated — restore now"
 *   expired       → red,    "Trial expired — add payment to continue"
 */
const SuspensionBanner = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [gracePeriodEndsAt, setGracePeriodEndsAt] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const res = await axiosInstance.get('/api/business');
        if (!mounted) return;
        const sd = res.data?.subscriptionDetails || {};
        setStatus(sd.status);
        setGracePeriodEndsAt(res.data?.billing?.gracePeriodEndsAt || null);
      } catch (err) {
        // Silently ignore — banner just won't show
      }
    };
    fetchStatus();
    // Refresh every 5 minutes in case status changes mid-session
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  if (dismissed) return null;
  if (!status || !['past_due', 'grace_period', 'suspended', 'deactivated', 'expired'].includes(status)) {
    return null;
  }

  const config = (() => {
    switch (status) {
      case 'past_due':
        return {
          bg: 'bg-orange-500 dark:bg-orange-600',
          message: 'Payment failed. We\'re retrying — please verify your payment method to avoid interruption.',
          cta: 'Update Payment',
          dismissible: true
        };
      case 'grace_period': {
        let daysLeft = null;
        if (gracePeriodEndsAt) {
          const diff = new Date(gracePeriodEndsAt) - new Date();
          daysLeft = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        }
        const urgent = daysLeft !== null && daysLeft <= 3;
        return {
          bg: urgent ? 'bg-red-600 dark:bg-red-700' : 'bg-red-500 dark:bg-red-600',
          message: daysLeft !== null
            ? `Payment overdue. Update your payment method within ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} or your account will be suspended.`
            : 'Payment overdue. Update your payment method to avoid suspension.',
          cta: 'Update Payment',
          dismissible: false
        };
      }
      case 'suspended':
        return {
          bg: 'bg-red-600 dark:bg-red-700',
          message: 'Account suspended. Regular users cannot sign in. Update your payment method to restore access.',
          cta: 'Restore Account',
          dismissible: false
        };
      case 'deactivated': {
        let deletionDays = null;
        if (gracePeriodEndsAt) {
          // gracePeriodEndsAt repurposed as deactivatedAt + 60 days for deletion deadline
          const diff = new Date(gracePeriodEndsAt) - new Date();
          deletionDays = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        }
        return {
          bg: 'bg-red-700 dark:bg-red-800',
          message: deletionDays !== null
            ? `Account deactivated. Your data will be permanently deleted in ${deletionDays} ${deletionDays === 1 ? 'day' : 'days'}. Restore now to keep your data.`
            : 'Account deactivated. Your data is preserved for a limited time. Update billing to restore access immediately.',
          cta: 'Restore Account',
          dismissible: false
        };
      }
      case 'expired':
        return {
          bg: 'bg-red-500 dark:bg-red-600',
          message: 'Trial expired. Add a payment method to continue using Aiinak.',
          cta: 'Add Payment',
          dismissible: false
        };
      default:
        return null;
    }
  })();

  if (!config) return null;

  return (
    <div className={`${config.bg} text-white shadow-md`}>
      <div className="max-w-[1920px] mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium truncate">{config.message}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/billing')}
              className="px-4 py-1.5 bg-white text-gray-900 hover:bg-gray-100 font-medium text-sm rounded-lg transition-colors whitespace-nowrap"
            >
              {config.cta}
            </button>
            {config.dismissible && (
              <button
                onClick={() => setDismissed(true)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuspensionBanner;
